import { useCallback, useEffect, useState } from 'react';
import { Dialog } from '../ui';
import { api } from '../../lib/api';

/**
 * DOC1b - putting a document that already exists onto the record it belongs to.
 *
 * ⛔⛔ THIS IS THE WRITE SURFACE JOSEPH'S ASYMMETRY GOVERNS: *"a wrongly-filed OM becomes an LOI
 * with the wrong building's numbers on a document carrying my DRE number."* Everything odd-looking
 * about this component is that sentence, and the measurements behind it (2026-09-05, live):
 *
 *     9 of the 10 distinct unfiled names carry an address   -> prefill is worth building
 *     the hint as ONE STRING found a record for 3 of 8      -> the backend searches by TOKENS
 *     `1101 E. 1ST STREET` -> 0 rows; `1101 E 1st Street` -> 1    SAME BUILDING, two filenames
 *     the house number `1101` alone matches                 -> FIVE buildings
 *     3 of the 15 name a property NOT IN THE DATABASE       -> "no match" is normal, not an error
 *     `813 N D St` is TWO live rows, differing only by CASE -> a name alone cannot be chosen from
 *
 * ⭐⭐ So the hint fills the SEARCH BOX and never the answer, every candidate wears what
 * distinguishes it, and the button that writes stays dead until he has picked one.
 */

export interface FileItDocument {
  id: string;
  filename: string;
  /** The address the FILENAME suggests, or null. A search term - never a selection. */
  filingHint: string | null;
  /**
   * The same address in two spellings gives the SAME key. ⭐⭐⭐ Measured: the eight Beaumont
   * documents carry TWO hint strings (`1101 E. 1ST STREET`, `1101 E 1st Street`) and one key, so
   * a bulk prefill that compared the TEXT would refuse exactly the group this feature is for.
   */
  filingHintKey: string | null;
}

/**
 * The one address a selection agrees on, or null.
 *
 * ⛔ Null the moment they disagree, or any of them names no address - a hint taken from one
 * document is a lie about the others, and this dialog's whole discipline is that a prefill is a
 * search rather than an answer.
 */
export const agreedHint = (docs: FileItDocument[]): string | null => {
  if (docs.length === 0) return null;
  const key = docs[0].filingHintKey;
  if (!key) return null;
  return docs.every((d) => d.filingHintKey === key) ? docs[0].filingHint : null;
};

interface Candidate {
  targetType: string;
  targetId: string;
  name: string;
  detail: string | null;
  documentCount: number;
}

type Kind = 'property' | 'master_property' | 'deal';
const KINDS: Array<{ key: Kind; label: string }> = [
  { key: 'master_property', label: 'Property Database' },
  { key: 'property', label: 'My Deals' },
  { key: 'deal', label: 'Deal' },
];

interface Props {
  open: boolean;
  /** One or many. DOC1d: eight of the fifteen unfiled documents belong to one building. */
  documents: FileItDocument[];
  onClose: () => void;
  onFiled: () => void;
}

export default function FileItDialog({ open, documents, onClose, onFiled }: Props) {
  const [kind, setKind] = useState<Kind>('master_property');
  const [term, setTerm] = useState('');
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [chosen, setChosen] = useState<Candidate | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  /** Which of a batch could not be filed. Named, never counted - see `submit`. */
  const [failed, setFailed] = useState<FileItDocument[]>([]);

  const doc = documents.length > 0 ? documents[0] : null;
  const hint = agreedHint(documents);
  const key = documents.map((d) => d.id).join(',');

  // Re-seeded whenever a different document is opened, so the box never carries the last one's
  // address into this one's search - which would be a hint pointing at the wrong building.
  useEffect(() => {
    if (!open || documents.length === 0) return;
    setTerm(hint ?? '');
    setChosen(null);
    setCandidates(null);
    setError('');
    setFailed([]);
    setKind('master_property');
    // Keyed on the whole SELECTION, not one id: changing which documents are selected has to
    // re-seed the box, or a hint from the previous set survives into a set it does not describe.
  }, [open, key, hint]);

  const search = useCallback(async (k: Kind, q: string) => {
    if (!q.trim()) { setCandidates(null); return; }
    try {
      const res = await api.searchFilingTargets(k, q.trim());
      setCandidates(res.candidates ?? []);
    } catch (e) {
      setCandidates([]);
      setError(e instanceof Error ? e.message : 'Could not search');
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => void search(kind, term), 250);
    return () => clearTimeout(t);
  }, [open, kind, term, search]);

  // ⛔ Changing the KIND clears the choice. A record chosen under one kind is not a record under
  // another, and carrying the selection across would file onto a target the list no longer shows.
  const chooseKind = (k: Kind) => { setKind(k); setChosen(null); };

  /**
   * ⭐⭐ ONE CALL PER DOCUMENT, and a PARTIAL result is reported as one.
   *
   * Each `POST` verifies the target on its own, so a record deleted mid-batch stops that document
   * and not the rest. Reporting "done" after six of eight would leave two silently unfiled, which
   * is the same invisibility the whole document programme exists to remove - so the failures are
   * NAMED. `linkFile` is idempotent per natural key, so retrying the batch re-files nothing.
   */
  const submit = async () => {
    if (!chosen || documents.length === 0) return;
    setBusy(true);
    setError('');
    setFailed([]);
    const missed: FileItDocument[] = [];
    let last = '';
    for (const d of documents) {
      try {
        await api.fileDocument(d.id, {
          targetType: chosen.targetType,
          targetId: chosen.targetId,
        });
      } catch (e) {
        missed.push(d);
        last = e instanceof Error ? e.message : 'Could not file the document';
      }
    }
    setBusy(false);
    if (missed.length === 0) { onFiled(); return; }
    setFailed(missed);
    setError(documents.length === 1
      ? last
      : `${documents.length - missed.length} filed, ${missed.length} could not be - ${last}`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={documents.length > 1 ? `File ${documents.length} documents` : 'File this document'}
      description={documents.length === 1 ? doc?.filename : `${documents.length} selected`}
      size="md"
      footer={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-gray-300 px-3 py-1.5 text-[12.5px] font-semibold text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!chosen || busy}
            className="rounded bg-gray-900 px-3 py-1.5 text-[12.5px] font-semibold text-white disabled:opacity-40"
          >
            {busy
              ? 'Filing...'
              : documents.length > 1 ? `File ${documents.length} documents here` : 'File it here'}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="flex flex-wrap gap-1.5">
          {KINDS.map((k) => (
            <button
              key={k.key}
              type="button"
              onClick={() => chooseKind(k.key)}
              className={`rounded border px-2.5 py-1 text-[12px] font-semibold ${
                kind === k.key
                  ? 'border-gray-900 bg-gray-900 text-white'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {k.label}
            </button>
          ))}
        </div>

        <div>
          <label htmlFor="fileit-search" className="block text-[12px] font-semibold text-gray-700">
            Search for the record
          </label>
          <input
            id="fileit-search"
            value={term}
            onChange={(e) => { setTerm(e.target.value); setChosen(null); }}
            placeholder="Address, or a deal name"
            className="mt-1 w-full rounded border border-gray-300 px-2.5 py-1.5 text-[13px]"
          />
          {hint ? (
            <p className="mt-1 text-[11.5px] text-gray-500">
              Prefilled from the file name{documents.length > 1 ? 's' : ''} - check it names the
              right building.
            </p>
          ) : documents.length > 1 ? (
            /* ⛔ Said out loud rather than left as an empty box: these documents do not agree on
               an address, so there is nothing honest to prefill. */
            <p className="mt-1 text-[11.5px] text-gray-500">
              These documents name different addresses, so nothing is prefilled.
            </p>
          ) : null}
        </div>

        {documents.length > 1 && (
          /* ⛔⛔ NAMED, NEVER COUNTED. At eight documents one wrong pick misfiles eight, and
             "File 8 documents" alone gives him nothing to check. */
          <ul className="max-h-28 overflow-y-auto rounded border border-gray-200 bg-gray-50 px-2.5 py-1.5">
            {documents.map((d) => (
              <li key={d.id} className="truncate text-[11.5px] text-gray-600" title={d.filename}>
                {d.filename}
              </li>
            ))}
          </ul>
        )}

        {/* ⭐ An empty result is a NORMAL answer here: three of the fifteen unfiled documents name
            a property that is not in the database at all. A blank panel would read as loading. */}
        {candidates !== null && candidates.length === 0 && (
          <p className="rounded border border-amber-300 bg-amber-50 px-2.5 py-2 text-[12px] text-amber-800">
            No matching record. Try a shorter search - or the property may not be in the database
            yet, which is true of three of the documents filed nowhere.
          </p>
        )}

        {candidates !== null && candidates.length > 0 && (
          <ul className="max-h-64 divide-y divide-gray-150 overflow-y-auto rounded border border-gray-200">
            {candidates.map((c) => {
              const picked = chosen?.targetId === c.targetId && chosen?.targetType === c.targetType;
              return (
                <li key={`${c.targetType}:${c.targetId}`}>
                  <button
                    type="button"
                    onClick={() => setChosen(c)}
                    aria-pressed={picked}
                    className={`block w-full px-2.5 py-2 text-left ${picked ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                  >
                    <span className="block text-[13px] font-medium text-gray-800">{c.name}</span>
                    {/* ⭐⭐ WHAT TELLS TWO CANDIDATES APART. `813 N D St` is two live rows that
                        differ only by case; the name alone cannot be chosen between. */}
                    <span className="block text-[11.5px] text-gray-500">
                      {[c.detail, c.documentCount > 0
                        ? `${c.documentCount} document${c.documentCount === 1 ? '' : 's'}`
                        : null].filter(Boolean).join(' · ') || 'No other details'}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {error && (
          <div className="rounded border border-red-300 bg-red-50 px-2.5 py-2 text-[12px] text-red-700">
            <p>{error}</p>
            {failed.length > 0 && (
              <ul className="mt-1 list-disc pl-4">
                {failed.map((d) => <li key={d.id} className="break-words">{d.filename}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
}
